/**
 * Fetch app/organization config from the **initial** API (`endpoints.configuration`).
 * GET **app_config** runs in parallel; see `data/domains/config/appConfig.ts` for typed keys and defaults.
 * Results are cached in module scope until `clearAppConfigCache()`.
 *
 * **Cold start:** `App.tsx` → `loadAppConfig()` (= `fetchInitialConfig()`) before navigation.
 * **Also:** main/onboarding screens and nav may call `fetchInitialConfig()` again; it no-ops once `cached` is set.
 *
 * **Globally readable accessors (after a successful initial fetch):**
 * - `getAppConfig()` — full cached **initial** envelope (`success`, `code`, `message`, `data`).
 * - `getGlobalConfig()` — `{ quote, a_quote, organization, is_customer_flow_enabled }` (derived from `data`).
 * - `getOrganization()` — `data.organization`.
 * - `getSettings()` — normalized org `configurations.settings` + org phone/referral fields.
 * - `getTheme()` — org `configurations.theme`.
 * - `getDateServiceConfig()` — numeric slice of settings for subscription/calendar.
 * - `getQuote()` — `{ quote, a_quote }` from `data`.
 * - `getAppConfigEndpointPayload()` — raw JSON from GET **app_config** (parallel fetch on cold start).
 * - `getMergedAppConfig()` — typed app_config keys with doc defaults (see `data/domains/config/appConfig.ts`).
 */
import { apiGet } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import {
  applyAppConfigPayload,
  resetAppConfigCache,
} from './appConfig';
import type { DateServiceConfig } from '../subscription/types';

/** Theme object from API (organization.configurations.theme). */
export interface ApiTheme {
  primary?: string;
  on_primary?: string;
  primary_container?: string;
  on_primary_container?: string;
  secondary?: string;
  on_secondary?: string;
  secondary_container?: string;
  on_secondary_container?: string;
  tertiary?: string;
  on_tertiary?: string;
  tertiary_container?: string;
  on_tertiary_container?: string;
  /** Optional macro chart colors (preferred). If omitted, legacy `on_*_container` keys or generated hues are used. */
  calorie?: string;
  protein?: string;
  carbs?: string;
  fats?: string;
  error?: string;
  on_error?: string;
  error_container?: string;
  on_error_container?: string;
  background?: string;
  on_background?: string;
  surface?: string;
  on_surface?: string;
  surface_variant?: string;
  on_surface_variant?: string;
  outline?: string;
  [key: string]: string | undefined;
}

/** organization.configurations.settings from API (values are strings in response). */
export interface ApiSettings {
  package_start_after?: string;
  menu_selection_before?: string;
  made_on_before?: string;
  delivery_cost?: string;
  minimum_order_cost?: string;
  expiration_notification_before_days?: string;
  maximum_allergy_selection?: string;
  maximum_dislike_selection?: string;
  maximum_subscribers?: string;
  low_nutrition_highlight?: string;
  average_nutrition_highlight?: string;
  high_nutrition_highlight?: string;
  week_day_off?: string;
  meal_double_box?: string;
  calculated_macros_for_mobile?: string;
  show_macros_for_mobile?: string;
  delivery_image_required?: string;
  maximum_addresses_allowed?: string;
  customer_recurring_days_limit?: string;
  [key: string]: string | undefined;
}

/** organization.configurations.general */
export interface ConfigGeneral {
  name?: string;
  website?: string;
  mail?: string;
  phone?: string;
  whatsapp_number?: string;
  address?: string;
  sender_mail?: string;
  receiver_mail?: string;
}

/** organization.configurations.assets */
export interface ConfigAssets {
  logo?: string;
  actual_logo?: string;
  favicon?: string;
  background?: string;
}

/** organization.configurations.social_media */
export interface ConfigSocialMedia {
  facebook_URL?: string;
  instagram_URL?: string;
  twitter_URL?: string;
  LinkedIn_URL?: string;
}

/** organization.configurations.loyalty */
export interface ConfigLoyalty {
  menu_loyalty?: string;
  subscription_loyalty?: string;
  single_point_redeem_value?: string;
  minimum_invoice_value_to_redeem_loyalty?: string;
}

/** organization.force_update (per platform) */
export interface ForceUpdatePlatform {
  current_version?: string;
  latest_version?: string;
  application_url?: string;
  type?: string;
}

/** organization.force_update.alerts */
export interface ForceUpdateAlerts {
  title?: string;
  description?: string;
  button_text?: string;
}

/** organization.force_update */
export interface ForceUpdate {
  ios?: ForceUpdatePlatform;
  android?: ForceUpdatePlatform;
  alerts?: ForceUpdateAlerts;
}

/** organization.configurations (fully typed) */
export interface OrganizationConfigurations {
  general?: ConfigGeneral;
  assets?: ConfigAssets;
  social_media?: ConfigSocialMedia;
  settings?: ApiSettings;
  theme?: ApiTheme;
  loyalty?: ConfigLoyalty;
}

/**
 * Organization object exactly as returned by the initial config API.
 * Field names and types match the API response (dialing_code is string e.g. "965").
 * Fields optional to allow partial responses.
 */
export interface ApiOrganization {
  id?: number;
  slug?: string;
  identifier?: string;
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_organization_expired?: boolean;
  /** Optional org contact id string; v1 uses lowercase **`shakir`** to gate Consultation entry points. */
  contact_person?: string;
  mail?: string;
  phone?: string;
  currency?: string;
  a_currency?: string;
  /** Phone dialing code; API returns string e.g. "965". */
  dialing_code?: string;
  allowed_digit_for_phone?: number;
  is_celebrity_feature_enabled?: number;
  is_loyalty_enabled?: number;
  is_referral_enabled?: number;
  is_calories_restricted?: number;
  is_customer_flow_enabled?: number;
  is_driver_flow_enabled?: number;
  is_owner_flow_enabled?: number;
  is_app_testing_mode?: number;
  is_addon_enabled?: number;
  is_recurring_feature?: string;
  is_sms_balance_notify?: string;
  is_menu_date_wise?: number;
  attachment?: string;
  is_active?: boolean;
  payment_gateway?: Array<{ name: string; display_name: string }>;
  payment_mode?: string;
  show_subscriptions?: string;
  show_online_orders?: string;
  show_actual_recipes?: number;
  address_fields?: string[];
  configurations?: OrganizationConfigurations;
  force_update?: ForceUpdate;
}

/** Data payload of initial config API (data: { quote, a_quote, organization }). */
export interface InitialConfigData {
  quote?: string;
  a_quote?: string;
  organization?: ApiOrganization;
}

/** Full initial config API response envelope. Matches response: success, code, message, data. */
export interface InitialConfigApiResponse {
  success?: boolean;
  code?: number;
  message?: string;
  data?: InitialConfigData;
}

/** Organization as exposed by getOrganization(); same shape as API. */
export type OrganizationConfig = ApiOrganization;

/** Normalized settings returned by getSettings(). Strict return type after transform. */
export interface NormalizedSettings {
  allowedDigitForPhone?: number;
  showReferralField?: boolean;
  /** From organization.dialing_code (normalized to number for display). */
  dialing_code?: number;
  /** Derived from dialing_code when known (e.g. 965 → "KW"); API does not send this. */
  country_iso_code?: string;
  menu_selection_before?: number;
  package_start_after?: number;
  week_day_off?: number;
  day_after?: number;
  expiration_notification_before_days?: number;
  maximum_allergy_selection?: number;
  maximum_dislike_selection?: number;
  maximum_subscribers?: number;
  maximum_addresses_allowed?: number;
  customer_recurring_days_limit?: number;
  delivery_cost?: number;
  minimum_order_cost?: number;
  made_on_before?: number;
  [key: string]: unknown;
}

/** Root data we store after fetch (quote, a_quote, organization). */
export interface GlobalConfig {
  quote: string;
  a_quote: string;
  organization: ApiOrganization;
  /** Merged from `data.is_customer_flow_enabled` (preferred) or `data.organization.is_customer_flow_enabled`. */
  is_customer_flow_enabled?: number | boolean | string;
}

/** Cached response type. data is set when fetch succeeded. */
export interface InitialConfigResponse {
  success: boolean;
  code: number;
  message: string;
  data: InitialConfigData | null;
}

const DEFAULT_DATE_CONFIG: DateServiceConfig = {
  menu_selection_before: 3,
  package_start_after: 2,
  week_day_off: 5,
};

let cached: InitialConfigResponse | null = null;
let loadPromise: Promise<InitialConfigResponse | null> | null = null;
let globalConfig: GlobalConfig | null = null;
let cachedAppConfigEndpointPayload: unknown | null = null;
let lastConfigLoadError: string | null = null;

/**
 * Fetch initial config (public endpoint). Caches response in memory.
 * Safe to call multiple times; only fetches once.
 */
export async function fetchInitialConfig(): Promise<InitialConfigResponse | null> {
  if (cached) {
    return cached;
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    try {
      lastConfigLoadError = null;
      const configuration_endpoint = (endpoints as { configuration?: string }).configuration ?? 'initial';
      const app_config_endpoint = (endpoints as { appConfig?: string }).appConfig ?? 'app_config';
      const initial_promise = apiGet<InitialConfigApiResponse>(configuration_endpoint);
      const app_config_promise = apiGet<unknown>(app_config_endpoint).catch(() => null);
      const [response, app_config_payload] = await Promise.all([initial_promise, app_config_promise]);
      cachedAppConfigEndpointPayload = app_config_payload;

      const data = response?.data;

      if (data && typeof data === 'object' && data.organization && typeof data.organization === 'object') {
        cached = {
          success: response?.success ?? true,
          code: response?.code ?? 200,
          message: response?.message ?? '',
          data: data as InitialConfigData,
        };
        globalConfig = {
          quote: data.quote ?? '',
          a_quote: data.a_quote ?? '',
          organization: data.organization,
          is_customer_flow_enabled: resolveIsCustomerFlowEnabledFromData(data) as number | boolean | string | undefined,
        };
        applyAppConfigPayload(app_config_payload, data.organization);
        try {
          const { useThemeStore } = require('../../../shared/theme/store') as typeof import('../../../shared/theme/store');
          const theme_state = useThemeStore.getState();
          if (theme_state.isInitialized) {
            const { refreshThemeForScheme } = require('../../../shared/theme/init') as typeof import('../../../shared/theme/init');
            refreshThemeForScheme(theme_state.appearance);
          }
        } catch {
          /* theme refresh is best-effort after config reload */
        }
      } else {
        cached = null;
        globalConfig = null;
        applyAppConfigPayload(app_config_payload, null);
      }
      return cached;
    } catch (err) {
      lastConfigLoadError = err instanceof Error ? err.message : String(err);
      cached = null;
      globalConfig = null;
      cachedAppConfigEndpointPayload = null;
      applyAppConfigPayload(null, null);
      return null;
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

/**
 * Prefer `data.is_customer_flow_enabled` (root); if absent, use `data.organization.is_customer_flow_enabled`.
 */
function resolveIsCustomerFlowEnabledFromData(data: InitialConfigData): number | boolean | string | undefined {
  const d = data as unknown as Record<string, unknown>;
  const root = d.is_customer_flow_enabled;
  if (root !== null && root !== undefined && String(root) !== '') {
    return root as number | boolean | string;
  }
  const org = d.organization;
  if (org != null && typeof org === 'object' && (org as Record<string, unknown>).is_customer_flow_enabled != null) {
    const o = (org as Record<string, unknown>).is_customer_flow_enabled;
    if (o !== null && o !== undefined && String(o) !== '') {
      return o as number | boolean | string;
    }
  }
  return undefined;
}

/** Returns the last config load error message, or null if none. Cleared on next successful load. */
export function getConfigLoadError(): string | null {
  return lastConfigLoadError;
}

/** Clear stored config load error (e.g. after user retries). */
export function clearConfigLoadError(): void {
  lastConfigLoadError = null;
}

/**
 * Sync read of cached config. Returns null if not yet loaded.
 */
export function getAppConfig(): InitialConfigResponse | null {
  return cached;
}

/**
 * Response body from GET **`app_config`** (same moment as first `fetchInitialConfig()`, via `Promise.all`).
 * Shape is backend-defined; map into typed getters after you finalize the contract. `null` if the request failed or returned null.
 */
export function getAppConfigEndpointPayload(): unknown | null {
  return cachedAppConfigEndpointPayload;
}

/**
 * Typed global config (quote, a_quote, organization). Populated when fetchInitialConfig() succeeds at app launch.
 */
export function getGlobalConfig(): GlobalConfig | null {
  return globalConfig;
}

/**
 * Organization object from cached config (payment_gateway, configurations, etc.).
 */
export function getOrganization(): OrganizationConfig | null {
  const org = cached?.data?.organization;
  return org && typeof org === 'object' ? org : null;
}

/**
 * v1-only: show Consultation entry points when initial API
 * `organization.contact_person` equals **`shakir`** (trimmed, case-insensitive).
 */
export function isConsultationEntryEnabledFromOrg(): boolean {
  const raw = getOrganization()?.contact_person;
  if (raw == null || typeof raw !== 'string') {
    return false;
  }
  return raw.trim().toLowerCase() === 'shakir';
}

const NUMERIC_SETTINGS_KEYS = [
  'package_start_after',
  'menu_selection_before',
  'made_on_before',
  'week_day_off',
  'expiration_notification_before_days',
  'maximum_allergy_selection',
  'maximum_dislike_selection',
  'maximum_subscribers',
  'maximum_addresses_allowed',
  'customer_recurring_days_limit',
  'delivery_cost',
  'minimum_order_cost',
];

function toNum(val: unknown): number | undefined {
  if (val === null || val === undefined) {return undefined;}
  const n = Number(val);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Settings for hooks and screens: normalized numbers from configurations.settings,
 * plus org-level fields (allowedDigitForPhone, showReferralField, dialing_code, country_iso_code).
 * Return type is strictly NormalizedSettings.
 */
export function getSettings(): NormalizedSettings {
  const raw = cached?.data?.organization?.configurations?.settings;
  const org = cached?.data?.organization;
  const base: Record<string, unknown> =
    raw && typeof raw === 'object'
      ? { ...raw }
      : {};

  const out: Record<string, unknown> = {};
  for (const key of Object.keys(base)) {
    const v = base[key];
    if (NUMERIC_SETTINGS_KEYS.includes(key) && (typeof v === 'string' || typeof v === 'number')) {
      const n = toNum(v);
      if (n !== undefined) {out[key] = n;}
      else {out[key] = v;}
    } else {
      out[key] = v;
    }
  }
  if (raw && typeof raw === 'object') {
    const psa = raw.package_start_after;
    if (psa !== undefined) {out.day_after = toNum(psa) ?? out.day_after;}
  }
  if (org && typeof org === 'object') {
    if (org.allowed_digit_for_phone != null) {out.allowedDigitForPhone = Number(org.allowed_digit_for_phone);}
    if (org.is_referral_enabled != null) {out.showReferralField = Boolean(Number(org.is_referral_enabled));}
    const raw_dialing_code = org.dialing_code;
    if (raw_dialing_code != null) {
      const dc = toNum(raw_dialing_code);
      if (dc !== undefined) {out.dialing_code = dc;}
      if (typeof raw_dialing_code === 'string') {
        out.country_iso_code = DIALING_CODE_TO_COUNTRY_ISO[raw_dialing_code] ?? undefined;
      }
    }
  }
  return out as NormalizedSettings;
}

/** Map dialing_code (string) to country ISO for APIs that need country_iso_code. API does not send country_iso_code. */
const DIALING_CODE_TO_COUNTRY_ISO: Record<string, string> = {
  '965': 'KW',
  '966': 'SA',
  '973': 'BH',
  '974': 'QA',
  '971': 'AE',
  '968': 'OM',
};

/**
 * Theme colors from API (organization.configurations.theme). Null if not loaded or missing.
 */
export function getTheme(): ApiTheme | null {
  const theme = cached?.data?.organization?.configurations?.theme;
  return theme && typeof theme === 'object' ? theme : null;
}

/**
 * Date-service config for subscription/calendar. Uses defaults if not loaded or missing keys.
 * API may send numeric settings as strings; normalized to numbers here.
 */
export function getDateServiceConfig(): DateServiceConfig {
  const settings = cached?.data?.organization?.configurations?.settings;
  if (!settings || typeof settings !== 'object') {
    return DEFAULT_DATE_CONFIG;
  }
  return {
    menu_selection_before:
      settings.menu_selection_before != null
        ? Number(settings.menu_selection_before)
        : DEFAULT_DATE_CONFIG.menu_selection_before,
    package_start_after:
      settings.package_start_after != null
        ? Number(settings.package_start_after)
        : DEFAULT_DATE_CONFIG.package_start_after,
    week_day_off:
      settings.week_day_off != null ? Number(settings.week_day_off) : DEFAULT_DATE_CONFIG.week_day_off,
  };
}

/**
 * Quote strings from root data (data.quote, data.a_quote). Null if not loaded.
 */
export function getQuote(): { quote?: string; a_quote?: string } | null {
  const data = cached?.data;
  if (!data || typeof data !== 'object') {return null;}
  return {
    quote: typeof data.quote === 'string' ? data.quote : undefined,
    a_quote: typeof data.a_quote === 'string' ? data.a_quote : undefined,
  };
}

/**
 * Clear cache (e.g. for tests or logout). Next fetchInitialConfig() will refetch.
 */
export function clearAppConfigCache(): void {
  cached = null;
  loadPromise = null;
  globalConfig = null;
  cachedAppConfigEndpointPayload = null;
  resetAppConfigCache();
  lastConfigLoadError = null;
}
